// BootSplash — what the app shows before it knows WHERE it is going.
//
// The bug this fixes: `showLauncher` started false and only flipped to true once
// the character list resolved, so `false` meant both "show the editor" and "we
// don't know yet". Boot therefore fell through to the `!character` branch and
// painted the ENTIRE docked shell — menubar, tool rail, outliner, viewport, dock,
// console — for as long as the list fetch took, then threw it away and replaced it
// with the launcher. An empty editor you never asked for, flashing on every load.
//
// The fix is a third state, and this is its screen: no shell chrome, because chrome
// implies a character is being loaded into it, and at this point no character has
// been chosen. That does NOT weaken the "chrome renders immediately and never
// unmounts during a character load" rule — that rule is about switching characters
// once a destination is known. Here there is no destination yet, and rendering
// chrome that is about to be replaced wholesale is precisely the flash.
//
// Styled as the launcher's own top bar over an empty body, because the launcher is
// where boot lands by default: the bar stays put and the content fills in, so the
// transition reads as one screen resolving rather than two screens fighting.
//
// Pure presentational — no stores, no fetches, no timers.

import React from 'react'
import { UI_FONT } from './PipelineGlyph.jsx'

const LINE = 'var(--m-line)'

export default function BootSplash({
  // Shown under the bar. Kept a prop rather than hardcoded so a slow phase can
  // say which one it is instead of a generic spinner that never changes.
  label = 'Loading characters…',
}) {
  return (
    <div
      data-testid="boot-splash"
      style={{
        width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
        overflow: 'hidden', background: 'var(--m-bg)', color: 'var(--m-fg)',
        fontFamily: 'var(--m-font)',
      }}
    >
      {/* Identical to CharacterLauncher's top bar, so when the launcher replaces
          this screen the bar does not move, blink, or re-draw. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0,
        padding: '11px 15px', borderBottom: `1px solid ${LINE}`, background: 'var(--m-panel)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{
            fontFamily: UI_FONT, fontSize: 12.5, letterSpacing: '.18em',
            fontWeight: 500, color: 'var(--m-fg)',
          }}>AVATAROS</span>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--m-accent)' }} />
        </div>
      </div>

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', gap: 14,
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Spinner />
        <div style={{ fontFamily: UI_FONT, fontSize: 11, letterSpacing: '.08em', color: 'var(--m-fg-3)' }}>
          {label}
        </div>
      </div>
    </div>
  )
}

/**
 * A rotating arc.
 *
 * CSS-animated rather than driven from JS: this screen is up while the main thread
 * is busy with the character fetch and the plugin load, and a compositor-driven
 * transform keeps turning through that. A JS-timed spinner would stutter in
 * exactly the moment it exists to cover.
 */
function Spinner() {
  return (
    <>
      <style>{`
        @keyframes aos-boot-spin { to { transform: rotate(360deg) } }
        .aos-boot-spinner {
          width: 22px; height: 22px; border-radius: 50%;
          border: 2px solid color-mix(in srgb, var(--m-fg) 14%, transparent);
          border-top-color: var(--m-accent);
          animation: aos-boot-spin .8s linear infinite;
        }
        /* Respect a reduced-motion preference: hold the arc still rather than
           spin it. The label already says what is happening. */
        @media (prefers-reduced-motion: reduce) {
          .aos-boot-spinner { animation: none }
        }
      `}</style>
      <div className="aos-boot-spinner" role="progressbar" aria-label="Loading" />
    </>
  )
}
